function convertPixelsIntoRems(sizeInPx) {
  const spacingUnitInRems = 0.25;
  const spacingUnitInPx = 4;

  const scale = sizeInPx / spacingUnitInPx;
  const rems = scale * spacingUnitInRems;
  return { [scale.toString()]: `${rems}rem` };
}

const colors = {
  'transparent': 'rgb(0, 0, 0, 0)',
  'ai-50': '#002d8b',
  'blue-0': '#ebfdfe',
  'blue-10': '#dafdff',
  'blue-30': '#99f8fd',
  'blue-50': '#00DCE8',
  'blue-60': '#30cad9',
  'blue-70': '#009fb5',
  'blue-80': '#008ea2',
  'blue-90': '#008193',
  'blue-90-05': '#f3f8f9',
  'blue-90-10': '#e8f2f4',
  'blue-90-15': '#d9ecef',
  'blue-90-20': '#d0e5e9',
  'orange-0': '#fef8f1',
  'orange-10': '#fff2e4',
  'orange-30': '#ffd8af',
  'orange-50': '#ffa84f',
  'orange-60': '#ff9126',
  'orange-70': '#e57400',
  'orange-80': '#cc6700',
  'orange-90': '#b55b00',
  'pink-0': '#fceff7',
  'pink-10': '#ffdfef',
  'pink-30': '#ffa4d1',
  'pink-50': '#ff3499',
  'pink-60': '#f9007b',
  'pink-70': '#e00070',
  'pink-80': '#c80061',
  'pink-90': '#ae0057',
  'navy-10': '#f8f9f9',
  'navy-20': '#f5f6f6',
  'navy-30': '#edf0f1',
  'navy-40': '#dce3e6',
  'navy-50': '#c0cdd4',
  'navy-60': '#b5bac4',
  'navy-70': '#6d778a',
  'navy-80': '#053956',
  'navy-90': '#04293e',
  'white': '#ffffff',
  'black': '#000000',
  'error': '#e00070',
  'white-op-30': 'rgb(255, 255, 255, 0.3)',
  'white-op-13': 'rgb(255, 255, 255, 0.13)',
  /* trust center branding styles */
  'tc-primary': 'rgb(var(--trust-primary-color, 5 57 86))',
  'tc-primary-5': 'rgb(var(--trust-primary-color, 5 57 86), 5%)',
  'tc-primary-10': 'rgb(var(--trust-primary-color, 5 57 86), 10%)',
  'tc-primary-20': 'rgb(var(--trust-primary-color, 5 57 86), 20%)',
  'tc-background': 'rgb(var(--trust-bg-color))',
  'tc-text': 'rgb(var(--trust-text-color, 255 255 255))',
};

module.exports = {
  important: true,
  purge: {
    enabled: false, // TODO: TEMPORARY DISABLED SINCE CSS GETS PURGED FOR STORYBOOK
    content: ['./src/**/*.{html,ts}']
  },
  theme: {
    colors: colors,
    backgroundColor: theme => ({
      ...theme('colors'),
      inherit: 'inherit',
    }),
    textColors: theme => ({
      ...theme('colors'),
      inherit: 'inherit',
    }),
    borderColor: theme => ({
      ...theme('colors')
    }),
    fill: (theme) => ({
      ...theme('colors'),
    }),
    stroke: (theme) => ({
      ...theme('colors'),
    }),
    fontFamily: {
      'main': 'Mulish',
      'secondary': 'Merriweather'
    },
    fontSize: {
      'xxs': ['9px', '150%'], // Format: '{{name}}': '{{size}}' | ['{{size}}', '{{line-height}}']
      'xs': ['10px', '150%'],
      'sm': ['11px', '150%'],
      'lg': ['12px', '150%'],
      'base': ['13px', '150%'],
      'xl': ['14px', '150%'],
      '2xl': ['16px', '150%'],
      '3xl': ['18px', '150%'],
      '4xl': ['21px', '150%'],
      '5xl': ['24px', '150%'],
      '6xl': ['26px', '150%'],
      '7xl': ['32px', '150%'],
      '8xl': ['34px', '150%'],
      '9xl': ['44px', '150%'],
    },
    minWidth: {
      'auto': 'auto',
      '0': '0',
      '1/4': '25%',
      '1/2': '50%',
      '3/4': '75%',
      'full': '100%',
      '150': '37.5rem', // 600px
      'fit': 'fit-content',
      ...convertPixelsIntoRems(416),
    },
    maxWidth: {
      'none': 'none',
      '0': '0',
      '1/4': '25%',
      '1/3': '33.3%',
      '2/3': '66.7%',
      '2/5': '40%',
      '1/2': '50%',
      '7/10': '70%',
      '3/4': '75%',
      '10/12': '83.333333%',
      'full': '100%',
      'fit': 'fit-content',
      'screen': '100vw',
      ...convertPixelsIntoRems(416),
      ...convertPixelsIntoRems(268),
    },
    minHeight: {
      'inherit': 'inherit',
      'auto': 'auto',
      'fit': 'fit-content',
    },
    maxHeight: {
      'none': 'none',
      'screen': '100vh',
      '2/5-dvh': '40dvh',
      'dvh': '100dvh',
    },
    boxShadow: {
      'none': 'none',
      'sm': '0px 2px 3px rgba(4, 41, 62, 0.1)',
      'md': '0px 4px 10px rgba(4, 41, 62, 0.2)',
      'lg': '0px 2px 13px rgba(4, 41, 62, 0.4)'
    },
    extend: {
      colors,
      borderRadius: {
        ...convertPixelsIntoRems(3),
        ...convertPixelsIntoRems(18),
        ...convertPixelsIntoRems(20),
        ...convertPixelsIntoRems(52)
      },
      spacing: {
        /*
          !!!IMPORTANT!!!
          Please, when you add new spacing here follow the approach described here - https://tailwindcss.com/docs/customizing-spacing
          For example, if you want to get size 13 (m-13, h-13, etc) that will result in 52px (because 13 * 4 = 52), and since
          4px is 0.25rem you need to convert your pixels taking into account this value. So, size 13 in rems will be 13 * 0.25 = 3.25.
          Please don't come up with your own scales.
          To faciliate this process just use convertPixelsIntoRems function in that file.
          There you need just to provide pixels and it will return object with correct scale value and appropriate REM value.
        */
        'inherit': 'inherit',
        ...convertPixelsIntoRems(1),
        ...convertPixelsIntoRems(3),
        ...convertPixelsIntoRems(5),
        '4.5': '1.125rem', // 18px
        '5.5': '1.375rem', // 22px
        '6.5': '1.625rem', // 26px
        '7.5': '1.875rem', // 30px
        '8.5': '2.125rem', // 34px
        '9.5': '2.375rem', // 38px
        '10.5': '2.625rem', // 42px
        '11.5': '2.875rem', // 46px
        ...convertPixelsIntoRems(50),
        ...convertPixelsIntoRems(52),
        '13.5': '3.375rem', // 54px
        '14.5': '3.625rem', // 58px
        '15': '3.75rem', // 60px
        '15.5': '3.875rem', // 62px
        '16.5': '4.125rem', // 66px
        '17': '4.25rem', // 68px
        '17.5': '4.375rem', // 70px
        '18': '4.5rem', // 72px
        '19': '4.75rem', // 76px
        '19.5': '4.875rem', // 78px
        '20.5': '5.125rem', // 82px
        '21.5': '5.375rem', // 86px
        '22': '5.5rem', // 88px,
        '22.5': '5.625rem', // 90px,
        '23': '5.75rem', // 92px
        '23.5': '5.875rem', // 94px
        '25': '6.25rem', // 100px
        '26': '6.5rem', // 104px
        '27': '6.75rem', // 108px
        '29': '7.25rem', // 116px
        '30': '7.5rem', // 120px
        '31': '7.75rem', // 124px
        '31.5': '7.875rem', // 126px
        '32.5': '8.125rem', // 130px
        '34': '8.5rem', // 136px
        '35': '8.75rem', // 140px
        '37': '9.25rem', // 148px
        '37.5': '9.375rem', // 150px
        '39': '9.75rem', // 156px
        ...convertPixelsIntoRems(170),
        '43': '10.75rem', // 172px
        '44.5': '11.125rem', // 178px
        '45': '11.25rem', // 180px
        '46': '11.5rem', // 184px
        '50': '12.5rem', // 200px
        ...convertPixelsIntoRems(210),
        '54': '13.5rem', // 216px
        '55': '13.75rem', // 220px
        ...convertPixelsIntoRems(224),
        ...convertPixelsIntoRems(232),
        '58.5': '14.625rem', // 234px
        '59': '14.75rem', // 236
        ...convertPixelsIntoRems(248),
        ...convertPixelsIntoRems(250),
        ...convertPixelsIntoRems(254),
        ...convertPixelsIntoRems(260),
        '68': '17rem', // 272px
        '70': '17.5rem', // 280px
        '72.5': '18.125rem', // 290px
        '75': '18.75rem', // 300px
        '83': '20.75rem', // 332px
        '85': '21.25rem', // 340px
        '86': '21.5rem', // 344px
        '89': '22.25rem', // 356px
        '91': '22.75rem', // 364px
        ...convertPixelsIntoRems(372),
        '100': '25rem', // 400px
        ...convertPixelsIntoRems(416),
        '106': '26.5rem', // 424px
        '107': '26.75rem', // 428px
        '108': '27rem', // 432px
        '110': '27.5rem', // 440px
        ...convertPixelsIntoRems(468),
        '125': '31.25rem', // 500px
        '128': '32rem', // 512px
        '132': '33rem', // 528px
        '135': '33.75rem', // 540px
        '136': '34rem', // 544px
        ...convertPixelsIntoRems(556),
        '144': '36rem', // 576px
        '146': '36.5rem', // 584px
        '150': '37.5rem', // 600px
        '151': '37.75rem', // 604px
        ...convertPixelsIntoRems(640),
        '175': '43.75rem', // 700px
        '179': '44.75rem', // 716px
        ...convertPixelsIntoRems(736),
        ...convertPixelsIntoRems(748),
        '192.5': '48.125rem', // 770px
        ...convertPixelsIntoRems(796),
        '200': '50rem', // 800px
        '250': '62.5rem', // 1000px
        '257.5': '64.375rem', // 1030px
        '269.5': '67.375rem', // 1078px
        '342': '85.5rem', // 1368px
        '450': '112.5rem', // 1800px
        '2/5': '40%',
      },
      minWidth: (theme) => ({
        ...theme('spacing'),
        max: 'max-content',
        full: '100%'
      }),
      maxWidth: (theme) => ({
        ...theme('spacing'),
        max: 'max-content',
        '9/10-vw': '90vw',
        full: '100%'
      }),
      minHeight: (theme) => ({
        ...theme('spacing'),
        full: '100%',
        dvh: '100dvh',
      }),
      maxHeight: (theme) => ({
        ...theme('spacing'),
        full: '100%',
        dvh: '100dvh',
      }),
      height: (theme) => ({
        ...theme('spacing'),
        fit: 'fit-content',
        dvh: '100dvh',
      }),
      width: (theme) => ({
        ...theme('spacing'),
        max: 'max-content',
        fit: 'fit-content',
      }),
      screens: {
        'xl': { 'max': '1560px' }
      },
      gridRowStart: {
        8: '8',
        9: '9',
        10: '10',
        11: '11',
        12: '12',
        13: '13',
      },
      gridColumnStart: {
        8: '8',
        9: '9',
        10: '10',
        11: '11',
        12: '12',
        13: '13',
      },
      backgroundImage: {
       'ai-gradient': 'linear-gradient(65deg, #00DCE8 -1.85%, #FF3499 71.72%, #FFA84F 100.94%)', 
       'ai-skeleton': 'linear-gradient(90deg, #D9FAFC -8.56%, #FFE1F0 75.4%, #FFF2E5 108.75%)',
      },
      verticalAlign: {
        'sub': 'sub',
      },
      opacity: {
        '15': '0.15',
      }
    },
  },
  variants: {
    extend: {
      visibility: ['group-hover'],
      pointerEvents: ['hover', 'focus'],
      display: ['group-hover', 'empty'],
    },
    padding: ['first', 'last'],
  },
  plugins: [
    ({ addBase, theme }) => {
      const customColors = theme('colors');
      const cssVariables = Object.entries(customColors).reduce((vars, [key, value]) => {
        if (typeof value === 'string') {
          vars[`--tw-${key}`] = value;
        }
        return vars;
      }, {});

      addBase({
        ':root': cssVariables,
      });
    },
    ({ addUtilities }) => {
      addUtilities({
        '.line-clamp-none': {
          display: 'block',
          '-webkit-box-orient': 'horizontal',
          overflow: 'visible',
          '-webkit-line-clamp': 'none',
        },
        '.line-clamp-1': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '1',
        },
        '.line-clamp-2': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '2',
        },
        '.line-clamp-3': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '3',
        },
        '.line-clamp-4': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '4',
        },
        '.line-clamp-5': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '5',
        },
        '.line-clamp-6': {
          display: '-webkit-box',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
          '-webkit-line-clamp': '6',
        },
      })
    }
  ],
};
